import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { FeedbackBuffer } from '../feedback-buffer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { loadArtworkTexture, coverScale } from '../artwork-texture.js';
import { vdjColor, vdjBeatChroma, vdjLoop, vdjCameraKick, VDJ_MIRROR_CHUNK, VdjPost, VdjComfort, VDJ_DROP_FLASH } from './lib/vdjLook.js';

// Contrat du rig partagé : interdit. Un liquide plein écran est sa propre lumière.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'acid',
  family: 'shader',
  danceability: 0.84,
  energyLevel: 0.78,
  bpmMin: 115,
  bpmMax: 150,
  movement: 0.80,
  tonality: 0.55,
  prefers: { gridStrength: [0.55, 1.0] },
  enabled: true
};

// Acid : un liquide psychédélique plein écran, des bandes arc-en-ciel qui
// coulent et s'enroulent comme de l'huile, la pochette dissoute dans le flux.
// Le trip liquide des boucles VJ, en version party - LOOK VDJ appliqué
// (lib/vdjLook.js) : noir et néon saturé, coupes sur la mesure, boucles de
// 32 temps, séparation RGB sur le temps par l'aberration de la passe filmic,
// miroir changé sur les phrases, à-coup de caméra sur la mesure, flash de
// drop à 0,85 sous le budget de confort.
//
// LE CHAMP : du domain warping à trois niveaux, un seul shader plein écran :
//   q = (fbm(p + t1), fbm(p + 5,2 + t1))
//   r = (fbm(p + 4q + 1,7 + t2), fbm(p + 4q + 9,2 + t2))
//   f = fbm(p + 4r)
// fbm = trois octaves de bruit de valeur, chacune UNE lecture d'une texture
// de bruit 256 x 256 filtrée (pas d'arithmétique de bruit). f s'enroule en
// volutes qui se replient sur elles-mêmes.
//
// LE DÉTAIL DÉCISIF : des BANDES, pas un dégradé. f est lu dans une palette
// cosinus à k bandes (6 à 14) : c = 0,5 + 0,5 cos(2π(k f + décalage + (0,
// 0,33, 0,67))), et un liseré sombre sur la dérivée de fract(k f) (fwidth)
// les rend nettes : de l'huile sur l'eau, du marbre psychédélique.
//
// DIFFÉRENCE AVEC LIQUIDLIGHT : lent et calme là-bas ; ici le liquide zoome
// au kick, coupe sur la mesure, et les bandes sont nettes et saturées.

const TIER = {
  0: { scale: 0.25, warps: 2 },
  1: { scale: 1 / 3, warps: 3 },
  2: { scale: 0.5, warps: 3 }
};
const K_RANGE = [6, 14];
const COVER_MIX = 0.30, COVER_MIX_BAR = 0.70;
const COVER_WARP = 0.18, COVER_WARP_BAR = 0.07;
const ZOOM_KICK = 0.06, ZOOM_ATTACK_S = 0.06, ZOOM_TAU_S = 0.1;   // retour en 300 ms (3 tau)
const ROTATION_BEATS = 32;
const T1_PER_BEAT = 0.045, T2_PER_BEAT = 0.07;
const DROP_BARS = 4;
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;
const SAFE_FLOOR = 0.5;
const EXPOSURE = 0.85;
const LUM_PERIOD_S = 0.5;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

const quadVertexShader = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;

function buildFragmentShader(L) {
  return `
  precision mediump float;
  uniform sampler2D uNoise;
  uniform sampler2D uCover;
  uniform float uHasCover;
  uniform vec2 uCoverScale;
  uniform float uAspect;
  uniform highp float uT1;
  uniform highp float uT2;
  uniform float uZoom;
  uniform float uRot;
  uniform float uK;
  uniform float uShift;
  uniform float uSeam;
  uniform float uCoverMix;
  uniform float uCoverWarp;
  uniform float uMirror;
  uniform float uExposure;
  uniform vec3 uPalA;
  uniform vec3 uPalB;
  uniform vec3 uPalD;
  uniform float uWarpView;
  uniform float uSafeAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  ${VDJ_MIRROR_CHUNK}

  // une octave = une lecture ; trois octaves sur trois canaux, décorrélées
  float fbm(highp vec2 p) {
    float v = 0.0;
    // une maille = une unité de p : trois à cinq mailles sur la hauteur
    v += 0.5 * texture2D(uNoise, p * (1.0 / 256.0)).r;
    v += 0.25 * texture2D(uNoise, p * (2.0 / 256.0) + 0.37).g;
    v += 0.125 * texture2D(uNoise, p * (4.0 / 256.0) + 0.71).b;
    return v / 0.875;
  }
  float safeAtt(vec2 uv) {
    vec2 ndc = uv * 2.0 - 1.0;
    vec2 s = vec2(ndc.x * uSafeAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }

  void main() {
    vec2 uv = vdjMirror(vUv, uMirror, uAspect);
    // p : le plan, zoomé au kick, tourné sur 32 temps
    highp vec2 p = (uv - 0.5) * vec2(uAspect, 1.0) * 3.0 * uZoom;
    float c0 = cos(uRot), s0 = sin(uRot);
    p = vec2(c0 * p.x - s0 * p.y, s0 * p.x + c0 * p.y);
    // le domain warping à trois niveaux
    vec2 q = vec2(fbm(p + uT1), fbm(p + 5.2 + uT1));
    vec2 r = vec2(fbm(p + 4.0 * q + 1.7 + uT2), fbm(p + 4.0 * q + 9.2 + uT2));
    ${L.warps >= 3 ? 'float f = fbm(p + 4.0 * r);' : 'float f = fbm(p + 4.0 * q + uT2 * 0.5); r = q;'}
    if (uWarpView > 0.5) {
      float v = uWarpView < 1.5 ? q.x : uWarpView < 2.5 ? r.x : f;
      gl_FragColor = vec4(vec3(v), 1.0); return;
    }
    // les BANDES : la palette cosinus lue à k bandes, un liseré sombre entre elles
    float kf = uK * f + uShift;
    float b = fract(kf);
    // la teinte : une par bande (un pas de 0,37 tour d'une bande à l'autre,
    // jamais deux voisines pareilles), qui glisse un peu dans la bande
    float hueT = (floor(kf) + 0.3 * b) * 0.37;
    vec3 col = max(uPalA + uPalB * cos(6.2831853 * (hueT + uPalD)), 0.0);
    // noir profond : chaque bande est une crête de néon sur un fond sombre,
    // le liseré net (fwidth) sur son flanc montant
    float w = max(fwidth(kf) * 1.5, 0.02);
    float seam = smoothstep(0.0, w, b) * smoothstep(1.0, 1.0 - w, b);
    float ridge = smoothstep(0.0, 0.10, b) * smoothstep(0.62, 0.32, b);
    float lit = mix(0.08 + 0.92 * ridge, (0.08 + 0.92 * ridge) * seam, uSeam);
    col *= lit;
    // le coeur blanc de la crête, au-dessus du seuil de bloom
    col += vec3(0.4) * smoothstep(0.8, 1.0, ridge) * smoothstep(0.08, 0.22, b) * seam;
    // la pochette dissoute : lue avec les UV déplacés par r
    if (uHasCover > 0.5) {
      vec2 cuv = (uv - 0.5) * uCoverScale + 0.5 + (r - 0.5) * uCoverWarp;
      vec3 cov = texture2D(uCover, cuv).rgb;
      // dissoute : la pochette prend le relief des bandes, les noirs restent
      col = mix(col, cov * (0.3 + 0.9 * lit), uCoverMix);
    }
    col *= uExposure;
    gl_FragColor = vec4(col * safeAtt(vUv), 1.0);
  }
  `;
}

const lumFragmentShader = `
  precision mediump float;
  uniform sampler2D uTex;
  varying vec2 vUv;
  void main() {
    vec3 c = texture2D(uTex, vUv).rgb;
    float l = dot(c, vec3(0.299, 0.587, 0.114));
    gl_FragColor = vec4(vec3(pow(l, 1.0 / 2.2)), 1.0);
  }
`;

const upscaleFragmentShader = `
  precision mediump float;
  uniform sampler2D uTex;
  varying vec2 vUv;
  void main() { gl_FragColor = vec4(texture2D(uTex, vUv).rgb, 1.0); }
`;

const MATERIALS = new Map();
let NOISE_TEXTURE = null;
function noiseTexture() {
  if (NOISE_TEXTURE) return NOISE_TEXTURE;
  const n = 256, data = new Uint8Array(n * n * 4);
  let seed = 1234567;
  for (let i = 0; i < data.length; i++) { seed = (seed * 1664525 + 1013904223) >>> 0; data[i] = seed >>> 24; }
  const t = new THREE.DataTexture(data, n, n, THREE.RGBAFormat, THREE.UnsignedByteType);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.minFilter = THREE.LinearFilter; t.magFilter = THREE.LinearFilter;
  t.generateMipmaps = false; t.needsUpdate = true;
  NOISE_TEXTURE = t;
  return t;
}

export class AcidVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._layout = TIER[this._tier] || TIER[1];
    const k = parseFloat(urlParam('acidk'));
    this._forceK = Number.isFinite(k) ? k : null;
    const cm = parseFloat(urlParam('acidcover'));
    this._forceCover = Number.isFinite(cm) ? clamp01(cm) : null;
    const wv = parseInt(urlParam('acidwarp'), 10);
    this._warpView = Number.isFinite(wv) ? wv : 0;
    const mi = parseInt(urlParam('acidmirror'), 10);
    this._forceMirror = Number.isFinite(mi) ? mi : null;
    // le look VDJ : l'aberration de la passe filmic pulsée au temps, le bloom
    // avec un seuil que les coeurs blancs dépassent
    // sans tone mapping : l'ACES tire le néon vers le blanc pastel ; ici les
    // crêtes clippent et les noirs restent noirs
    this._post = new VdjPost({ bloomStrength: 0.65, bloomThreshold: 0.78, grain: 0.015, vignette: 0.5, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    this._comfort = new VdjComfort();
  }

  get postProcessing() { return this._post.value; }

  _random() { this._seed = (this._seed * 1664525 + 1013904223) >>> 0; return this._seed / 4294967296; }

  init(scene, camera) {
    this._scene = scene;
    this._seed = 31;
    this._time = 0; this._paused = false;
    const L = this._layout;
    const key = `${this._tier}:${L.warps}`;
    let mat = MATERIALS.get(key);
    if (!mat) {
      mat = new THREE.ShaderMaterial({
        uniforms: {
          uNoise: { value: noiseTexture() }, uCover: { value: null }, uHasCover: { value: 0 }, uCoverScale: { value: new THREE.Vector2(1, 1) },
          uAspect: { value: 1.78 }, uT1: { value: 0 }, uT2: { value: 0 }, uZoom: { value: 1 }, uRot: { value: 0 },
          uK: { value: 8 }, uShift: { value: 0 }, uSeam: { value: 0.6 }, uCoverMix: { value: COVER_MIX }, uCoverWarp: { value: COVER_WARP },
          uMirror: { value: 0 }, uExposure: { value: EXPOSURE },
          uPalA: { value: new THREE.Vector3(0.5, 0.5, 0.5) }, uPalB: { value: new THREE.Vector3(0.5, 0.5, 0.5) }, uPalD: { value: new THREE.Vector3(0, 0.33, 0.67) },
          uWarpView: { value: 0 },
          uSafeAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter,
          uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
        },
        vertexShader: quadVertexShader, fragmentShader: buildFragmentShader(L), depthTest: false, depthWrite: false
      });
      MATERIALS.set(key, mat);
    }
    this._mat = mat; this._u = mat.uniforms;
    this._u.uWarpView.value = this._warpView;
    this._u.uCover.value = null; this._u.uHasCover.value = 0;
    this._buffer = new FeedbackBuffer({ scale: L.scale, single: true });
    this._volScene = new THREE.Scene();
    this._volQuad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mat);
    this._volQuad.frustumCulled = false;
    this._volScene.add(this._volQuad);
    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10);
    this._ortho.position.z = 1;
    this._upU = { uTex: { value: null } };
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._upU, vertexShader: quadVertexShader, fragmentShader: upscaleFragmentShader, depthTest: false, depthWrite: false }));
    this._display.frustumCulled = false; this._display.renderOrder = -1000;
    const self = this;
    this._display.onBeforeRender = function (renderer) { self._render(renderer); };
    // la mesure de confort : la luminance moyenne, lue pour de vrai. Le
    // demi-cadre est réduit à 16 x 16 texels (une lecture bilinéaire chacun,
    // luminance encodée sRGB = ce que l'écran affiche) et relu deux fois par
    // seconde : 1 Ko, un petit décrochage, et une mesure honnête plutôt
    // qu'une estimation.
    this._lumTarget = new THREE.WebGLRenderTarget(16, 16, { depthBuffer: false, stencilBuffer: false });
    this._lumPixels = new Uint8Array(16 * 16 * 4);
    this._lumU = { uTex: { value: null } };
    this._lumScene = new THREE.Scene();
    const lumQuad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._lumU, vertexShader: quadVertexShader, fragmentShader: lumFragmentShader, depthTest: false, depthWrite: false }));
    lumQuad.frustumCulled = false;
    this._lumScene.add(lumQuad);
    this._lumAge = 0; this._lumMeasured = 0.3; this._lumPending = false;
    scene.add(this._display);

    // l'état musical
    this._phaseBeat = null; this._phaseBar = null; this._phasePhrase = null; this._free = 0; this._beats = 0; this._bars = 0; this._secondsBeat = 0.5;
    this._beatCont = 0;                 // temps continus, pour les boucles de 32
    this._low = 0.5; this._mid = 0.4; this._high = 0.4;
    this._zoomAge = 10; this._barAge = 10; this._t1 = 0; this._t2 = 0; this._shift = 0;
    this._k = lerp(K_RANGE[0], K_RANGE[1], this._random()); this._kTarget = this._k;
    this._mirror = 0; this._dropUntil = -1; this._buildup = 0;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null;
    this._paletteSeen = undefined; this._coverUrl = undefined; this._coverTex = null;
  }

  _applyPalette(profile) {
    const pal = profile.artworkPalette;
    if (pal === this._paletteSeen) return;
    this._paletteSeen = pal;
    const v = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors : [0xff2bd6, 0x2bffea];
    // deux couleurs vives, saturées par le look VDJ : la palette cosinus
    // tourne autour de leur moyenne (A) avec leur écart pour amplitude (B)
    const c1 = vdjColor(v[0]), c2 = vdjColor(v[1 % v.length]);
    const hsl = { h: 0, s: 0, l: 0 }; c1.getHSL(hsl);
    // noir profond et néon : A bas (les creux de chaque canal passent sous
    // zéro et se clampent en noir), B large (les crêtes montent à ~0,9)
    const m = new THREE.Color(0.5 * (c1.r + c2.r), 0.5 * (c1.g + c2.g), 0.5 * (c1.b + c2.b));
    this._u.uPalA.value.set(0.45 * m.r, 0.45 * m.g, 0.45 * m.b);
    this._u.uPalB.value.set(0.45 * m.r + 0.35, 0.45 * m.g + 0.35, 0.45 * m.b + 0.35);
    this._u.uPalD.value.set(hsl.h, hsl.h + 0.33, hsl.h + 0.67);
  }

  _loadCover(profile) {
    if (profile.artworkUrl === this._coverUrl) return;
    this._coverUrl = profile.artworkUrl;
    if (this._coverTex) { this._coverTex.dispose(); this._coverTex = null; }
    this._u.uHasCover.value = 0;
    if (!profile.artworkUrl) return;
    const self = this, url = profile.artworkUrl;
    loadArtworkTexture(url, { wrap: THREE.MirroredRepeatWrapping, mipmaps: true }, (tex) => {
      if (!self._scene || url !== self._coverUrl) { tex.dispose(); return; }
      self._coverTex = tex; self._u.uCover.value = tex; self._u.uHasCover.value = 1;
      const img = tex.image;
      self._coverAspect = img && img.width ? img.width / img.height : 1;
      self._u.uCoverScale.value.copy(coverScale(self._u.uAspect.value, self._coverAspect));
    }, () => {});
  }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) this._dropIdx = 0;
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) {
      if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true;
      this._dropIdx++;
    }
    return fired;
  }

  onTrackChange(profile) { this._applyPalette(profile); this._loadCover(profile); this._dropIdx = 0; this._lastNowMs = null; }

  update(deltaTime, profile) {
    this._paused = profile.playbackState !== 'PLAYING';
    if (this._paused) return;                     // pause : gel
    const dt = Math.min(deltaTime, 0.1);
    this._time += dt;
    this._applyPalette(profile);
    this._loadCover(profile);
    const director = profile.director;
    const bands = (director && director.hasReliableGrid && director.bands) ? director.bands : null;
    if (bands) { this._low = clamp01(bands.low); this._mid = clamp01(bands.mid); this._high = clamp01(bands.high); }
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    this._secondsBeat = bpm > 0 ? 60 / bpm : 0.5;
    const barS = 4 * this._secondsBeat;

    let beat = false, bar = false, phrase = false;
    const bp = profile.beatPhase;
    if (bp != null) { if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) beat = true; this._phaseBeat = bp; }
    else { this._free += dt; if (this._free >= this._secondsBeat) { this._free -= this._secondsBeat; beat = true; } }
    const barPhase = profile.barPhase;
    if (barPhase != null) { if (this._phaseBar != null && barPhase < this._phaseBar - 0.5) bar = true; this._phaseBar = barPhase; }
    else if (beat && (this._beats + 1) % 4 === 0) bar = true;
    const ph = profile.phrasePhase;
    if (ph != null) { if (this._phasePhrase != null && ph < this._phasePhrase - 0.5) phrase = true; this._phasePhrase = ph; }
    else if (beat && (this._beats + 1) % 16 === 0) phrase = true;
    if (beat) this._beats++;
    if (bar) this._bars++;
    // les temps continus : la rotation boucle sur exactement 32 temps
    this._beatCont = bp != null ? this._beats + bp : this._beats + this._free / this._secondsBeat;

    // la montée : un drop connu qui approche, ou le segment « build »
    const next = director && director.nextEvent;
    let build = 0;
    if (next && next.type === 'drop' && next.msUntil < 8000) build = 1 - next.msUntil / 8000;
    if (director && director.mode === 'build') build = Math.max(build, 0.6);
    this._buildup += (build - this._buildup) * (1 - Math.exp(-dt / 1.0));

    // DROP : coupe de palette (inversion, +0,5), k au maximum pendant 4 mesures, flash
    if (this._tickDrops(profile)) {
      this._shift += 0.5;
      this._dropUntil = this._time + DROP_BARS * barS;
      if (director && this._comfort.requestFlash(this._time)) director.flash = VDJ_DROP_FLASH;
    }
    const inDrop = this._time < this._dropUntil;

    // TEMPS : zoom (60 ms de montée, retour en 300 ms), cran de palette
    if (beat) { this._zoomAge = 0; this._shift += 1 / Math.max(1, this._k); }
    this._zoomAge += dt;
    const za = this._zoomAge;
    const zoomEnv = za < ZOOM_ATTACK_S ? za / ZOOM_ATTACK_S : Math.exp(-(za - ZOOM_ATTACK_S) / ZOOM_TAU_S);
    const zoomKick = ZOOM_KICK * (0.5 + 0.8 * this._low) * zoomEnv;
    // MESURE : la pochette remonte (70 % pendant un temps, déplacement réduit), à-coup de caméra
    if (bar) this._barAge = 0;
    this._barAge += dt;
    const barEnv = this._barAge < this._secondsBeat ? 1 : 0;
    // PHRASE : k change, miroir suivant - en coupe, sur le temps fort
    if (phrase) {
      this._kTarget = lerp(K_RANGE[0], K_RANGE[1], this._random());
      this._k = this._kTarget;
      this._mirror = (this._mirror + 1) % 4;
    }
    const kick = vdjCameraKick(barPhase != null ? barPhase : this._barAge / barS, this._bars, 4);

    // le mouvement : t1 et t2 au tempo, t2 s'emballe en montée
    this._t1 += dt / this._secondsBeat * T1_PER_BEAT;
    this._t2 += dt / this._secondsBeat * T2_PER_BEAT * (0.6 + 0.8 * this._mid) * (1 + 2.5 * this._buildup);

    const U = this._u;
    U.uT1.value = this._t1 % 64; U.uT2.value = this._t2 % 64;
    // le zoom : l'inverse (le plan rétrécit = l'image grossit), avec l'à-coup de la mesure
    U.uZoom.value = 1 / ((1 + zoomKick) * kick.zoom);
    U.uRot.value = vdjLoop(this._beatCont, ROTATION_BEATS) * Math.PI * 2 + kick.roll;
    U.uK.value = this._forceK != null ? this._forceK : (inDrop ? K_RANGE[1] : this._k);
    U.uShift.value = this._shift;
    U.uSeam.value = 0.45 + 0.5 * this._high;
    U.uCoverMix.value = this._forceCover != null ? this._forceCover : (barEnv ? COVER_MIX_BAR : COVER_MIX);
    U.uCoverWarp.value = barEnv ? COVER_WARP_BAR : COVER_WARP;
    U.uMirror.value = this._forceMirror != null ? this._forceMirror : this._mirror;
    U.uAspect.value = safeZoneUniforms.uAspect.value || 1.78;
    if (this._coverTex) U.uCoverScale.value.copy(coverScale(U.uAspect.value, this._coverAspect || 1));
    // le confort : la luminance moyenne estimée (palette cosinus ~0,5 x liseré x exposition)
    this._lumAge += dt;
    if (this._lumAge >= LUM_PERIOD_S) { this._lumAge = 0; this._lumPending = true; }
    // la mesure est prise après le gain courant, comme measure() l'attend
    U.uExposure.value = EXPOSURE * this._comfort.measure(this._lumMeasured, dt);
    // la séparation RGB sur le temps : l'aberration de la passe filmic
    this._post.update(vdjBeatChroma(bp != null ? bp : this._free / this._secondsBeat, this._secondsBeat));
  }

  _render(renderer) {
    const prev = renderer.getRenderTarget();
    this._buffer.ensureSize(renderer);
    renderer.setRenderTarget(this._buffer.read);
    renderer.render(this._volScene, this._ortho);
    renderer.setRenderTarget(prev);
    this._upU.uTex.value = this._buffer.read.texture;
    if (this._lumPending) {
      this._lumPending = false;
      this._lumU.uTex.value = this._buffer.read.texture;
      renderer.setRenderTarget(this._lumTarget);
      renderer.render(this._lumScene, this._ortho);
      renderer.readRenderTargetPixels(this._lumTarget, 0, 0, 16, 16, this._lumPixels);
      renderer.setRenderTarget(prev);
      let sum = 0;
      for (let i = 0; i < this._lumPixels.length; i += 4) sum += this._lumPixels[i];
      this._lumMeasured = sum / (256 * 255);
    }
  }

  dispose() {
    if (this._scene && this._display) { this._display.onBeforeRender = null; this._scene.remove(this._display); this._display.geometry.dispose(); this._display.material.dispose(); }
    if (this._buffer) this._buffer.dispose();
    if (this._lumTarget) { this._lumTarget.dispose(); this._lumTarget = null; }
    if (this._volQuad) this._volQuad.geometry.dispose();
    if (this._coverTex) { this._coverTex.dispose(); this._coverTex = null; }
    this._u.uCover.value = null; this._u.uHasCover.value = 0;
    this._scene = null;
  }
}
